/**
 * Daily-apply paywall relaxation experiment (T5b).
 *
 * The daily-apply cap (`/out` route redirect — see
 * `src/app/[locale]/out/[jobId]/[[...slug]]/route.ts`) is the product's
 * highest-converting upgrade surface, but it's also the highest-friction one:
 * it hard-blocks any authenticated free/open_profile user who applies past
 * `plan.dailyApplies` in a day, mid-application-flow. A standing product
 * directive currently FREEZES new monetization friction while W1/W4 retention
 * is too low — this experiment does the opposite of adding friction: it
 * measures whether relaxing the wall costs more retention than it earns in
 * upgrade conversion, for a flagged cohort only. LIVE in PostHog since
 * 2026-07-12 as a 50/50 control/variant split (`active: true`) — this was
 * NOT gated off as this comment previously (incorrectly) claimed; the
 * monitor watching it (`scripts/ops/check-arm-starvation.ts`) was reading a
 * signal that structurally could never show the variant arm, which made the
 * live experiment look starved. See incident-triage-execution-2026-08-02.md
 * (T12) for the monitor fix — the flag itself never needed a launch.
 *
 * `control` keeps today's hard wall (daily-cap redirect to `/pricing`,
 * `canApply === false` Lock-icon block). `variant` lets the user through —
 * client-side, `job-apply-cta.tsx` renders the real apply button (same
 * markup as the `canApply === true` branch, soft Pro upsell included, NOT
 * removed — only the hard block is removed) instead of the Lock wall;
 * server-side, `out/[jobId]/[[...slug]]/route.ts` skips the `/pricing`
 * redirect when `quota.allowed` is false.
 *
 * This SAME flag key is evaluated on BOTH sides, but via two different
 * PostHog read paths that must independently resolve the same per-user
 * decision:
 *   - Client: `useExperiment<boolean>` (this file's `APPLY_PAYWALL_RELAXED_VARIANTS`)
 *     — `job-apply-cta.tsx`.
 *   - Server: `getServerFeatureFlagForUser` (per-user, NOT the cached global
 *     helpers) — mirrors the exact call pattern `resolveFreeDailyApplies` uses
 *     for the sibling `apply-cap-tightened` flag in `src/lib/entitlements.ts`:
 *
 *       const { enabled } = await getServerFeatureFlagForUser(
 *         APPLY_PAYWALL_RELAXED_FLAG,
 *         session.user.id,
 *       );
 *
 * PostHog feature flag "apply-paywall-relaxed" (multivariate: control/variant,
 * `evaluation_runtime: "all"` so both the client `/decide` response and the
 * server-side per-user read resolve it) is the SOLE switch — see
 * `CLAUDE.md → Forbidden Actions` (never shadow a flag with a hardcoded
 * constant). Server-side `gate_impression` events on the daily-apply wall
 * carry a `paywall_relaxed` boolean property (added explicitly — the
 * server-side capture does NOT get PostHog's automatic `$feature/` tagging,
 * that only happens client-side after `posthog-js` resolves the flag), so the
 * funnel is analyzable by arm on both sides even when the redirect doesn't fire.
 */

export const APPLY_PAYWALL_RELAXED_FLAG = "apply-paywall-relaxed";

export const APPLY_PAYWALL_RELAXED_VARIANTS = {
	control: false,
	variant: true,
} as const;
